// One shared, retrying copy of the template catalog (GET /api/templates/catalog) for every view that explains a
// legacy "[name] params" row: the ticket pane, the read-only conversation pane, the Inbox list and the customer
// panel. Replaces the per-view one-shot fetches whose failure was silently ignored (the transcript then showed the
// raw "[name]" for the life of the view). New template sends carry their own snapshot and never need this.

import { useSyncExternalStore } from 'react';
import { getTemplateCatalog, type CatalogTemplate } from '../api/templates';

export type TemplateCatalogState =
  | { status: 'loading' }
  | { status: 'ready'; templates: readonly CatalogTemplate[] }
  | { status: 'failed' };

const RETRY_DELAYS_MS = [2_000, 10_000, 30_000];
const REFRESH_AFTER_MS = 10 * 60_000;

let state: TemplateCatalogState = { status: 'loading' };
let started = false;
let inFlight = false;
let attempt = 0;
let loadedAt = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function publish(next: TemplateCatalogState): void {
  state = next;
  for (const l of listeners) l();
}

async function load(): Promise<void> {
  if (inFlight) return;
  inFlight = true;
  try {
    const r = await getTemplateCatalog();
    attempt = 0;
    loadedAt = Date.now();
    publish({ status: 'ready', templates: r.templates });
  } catch {
    // Keep a previously loaded catalog; otherwise say so (rows show "not available") and try again.
    if (state.status !== 'ready') publish({ status: 'failed' });
    const delay = RETRY_DELAYS_MS[attempt];
    attempt += 1;
    if (delay !== undefined) {
      retryTimer = setTimeout(() => { retryTimer = null; void load(); }, delay);
    }
  } finally {
    inFlight = false;
  }
}

function onWake(): void {
  // After the bounded retries: try again when the agent comes back to the tab or the network returns; refresh a
  // stale catalog the same way.
  if (retryTimer) return;
  if (state.status === 'failed' || (state.status === 'ready' && Date.now() - loadedAt > REFRESH_AFTER_MS)) {
    attempt = 0;
    void load();
  }
}

function ensureStarted(): void {
  if (started) return;
  started = true;
  void load();
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);
  }
}

function subscribe(listener: () => void): () => void {
  ensureStarted();
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function useTemplateCatalog(): TemplateCatalogState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/** Tests only. */
export function _resetTemplateCatalog(): void {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  state = { status: 'loading' };
  started = false;
  inFlight = false;
  attempt = 0;
  loadedAt = 0;
  listeners.clear();
  if (typeof window !== 'undefined') {
    window.removeEventListener('focus', onWake);
    window.removeEventListener('online', onWake);
  }
}
